/**
 * OD FP/FN 逐 case 明细。
 *
 * 与通用 ``CaseDetailTable`` 的区别：OD FP/FN 是 issue_related 业务，它上报的
 * ``tp/fn/fp`` 是为 schema-2 metrics 协议做的映射，不是感知检测本身的 TP/FN/FP。
 * 这里直接消费 ``/perception/report/od-fpfn`` 的结构化计数，避免把映射值继续
 * 伪装成通用检测指标。
 */
import { useState } from 'react';
import { App, Button, Image, Space, Spin, Table, Tag, Tooltip } from 'antd';
import { FileTextOutlined, LinkOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import type { OdFpfnCase, PerceptionCaseDetailRow } from '@/apis/simulation';
import ApiCodeLink from '@components/ApiCodeLink';
import { buildFoxgloveUrl, resolveMcapUrl } from '@/common/foxglove';
import { EMPTY, fmtCount, fmtRatio } from '../format';

const PREVIEW_HEIGHT = 72;
const PREVIEW_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="128" height="72"><rect width="100%" height="100%" fill="#f2f3f5"/><text x="50%" y="50%" fill="#9b9da3" font-size="12" text-anchor="middle" dominant-baseline="middle">暂无预览</text></svg>',
  );

/** 与 runtime/backend 口径一致：aligned_frame_count / annotation_frame_count。 */
const frameAlignmentRatio = (m: OdFpfnCase['metrics']) => {
  const n = m.alignedFrameCount;
  const d = m.annotationFrameCount;
  return n !== null && n !== undefined && d ? n / d : null;
};

const matchedFrameRatio = (m: OdFpfnCase['metrics']) => {
  const n = m.matchedFrameCount;
  const d = m.eligibleAlignedFrameCount;
  return n !== null && n !== undefined && d ? n / d : null;
};

/**
 * 不通过原因：优先展示 runtime 上报的 failReasonCounts（reason×次数）。
 *
 * 老数据或帧级失败（如帧对齐率不足）可能没有 reason 分类，但 case 已
 * 判定不通过；此时按结构化计数推导兜底原因，避免空列误导用户。
 */
const reasonSummary = (
  reasons: Record<string, number>,
  row: OdFpfnCase,
  isFp: boolean,
  frameAlignmentRatio: (m: OdFpfnCase['metrics']) => number | null,
  matchedFrameRatio: (m: OdFpfnCase['metrics']) => number | null,
) => {
  const entries = Object.entries(reasons || {}).filter(([, n]) => n > 0);
  if (entries.length > 0) {
    return entries.map(([name, n]) => `${name}×${n}`).join('、');
  }
  if (row.passed) return EMPTY;

  const fallback: string[] = [];
  const alignment = frameAlignmentRatio(row.metrics);
  const matched = matchedFrameRatio(row.metrics);
  if (alignment !== null && alignment < 0.7) {
    fallback.push(`帧对齐率不足(${(alignment * 100).toFixed(1)}%<70%)`);
  }
  if (!isFp && matched !== null && matched < 0.9) {
    fallback.push(`匹配帧率不足(${(matched * 100).toFixed(1)}%<90%)`);
  }
  return fallback.length > 0 ? fallback.join('、') : 'checker不通过';
};

export default function OdFpfnCaseTable({
  cases,
  isFp,
  category,
  categoryRows,
  previewUrlMap,
  previewLoading = false,
  onViewLog,
}: {
  cases: OdFpfnCase[];
  /** FP/FN 只影响列名，不改计算。 */
  isFp: boolean;
  /** 当前展开的类别；ALL 展示所有 case。 */
  category: string;
  /** 该类别的通用 case-detail 行。按 caseId 建索引，用于判断 case 是否属于该类别。 */
  categoryRows: PerceptionCaseDetailRow[];
  previewUrlMap?: Record<number, string>;
  previewLoading?: boolean;
  onViewLog: (taskId: number) => void;
}) {
  const { message } = App.useApp();
  const [foxgloveLoadingTaskId, setFoxgloveLoadingTaskId] = useState<number | null>(null);
  const categoryRowsByCaseId = new Map(categoryRows.map((r) => [r.caseId || '', r]));
  const visibleCases = category === 'ALL'
    ? cases
    : cases.filter((r) => {
      const row = categoryRowsByCaseId.get(r.caseId || '');
      return row !== undefined && (row.gt ?? 0) > 0;
    });

  const openFoxglove = async (row: OdFpfnCase) => {
    if (!row.caseId) return;
    setFoxgloveLoadingTaskId(row.taskId);
    try {
      const url = await resolveMcapUrl(row.taskId, row.caseId);
      if (!url) {
        message.warning('未找到对应的 mcap 文件');
        return;
      }
      window.open(buildFoxgloveUrl(url, row.taskId), '_blank');
    } catch {
      message.error('获取 mcap 文件链接失败');
    } finally {
      setFoxgloveLoadingTaskId(null);
    }
  };

  const columns: ColumnsType<OdFpfnCase> = [
    {
      title: 'Case',
      dataIndex: 'caseId',
      key: 'caseId',
      width: 160,
      fixed: 'left',
      render: (v: string | null) => v || EMPTY,
    },
    {
      title: '状态',
      key: 'passed',
      width: 90,
      render: (_, r) => (
        <Tag color={r.passed ? 'success' : 'error'}>{r.passed ? '通过' : '不通过'}</Tag>
      ),
    },
    ...(previewUrlMap
      ? [
          {
            title: '预览',
            key: 'preview',
            width: 150,
            render: (_: unknown, r: OdFpfnCase) => {
              const url = previewUrlMap[r.taskId];
              if (!url) {
                return (
                  <span style={{ color: '#9b9da3', fontSize: 12 }}>
                    {previewLoading ? '加载中…' : '暂无预览'}
                  </span>
                );
              }
              return (
                <Image
                  src={url}
                  height={PREVIEW_HEIGHT}
                  loading="lazy"
                  style={{ objectFit: 'contain', maxWidth: 130 }}
                  fallback={PREVIEW_FALLBACK}
                />
              );
            },
          },
        ]
      : []),
    {
      title: 'FOXGLOVE',
      key: 'foxglove',
      width: 110,
      render: (_, r) => {
        const loadingThis = foxgloveLoadingTaskId === r.taskId;
        const disabled = !r.taskId || !r.caseId || loadingThis;
        return (
          <Tooltip title={disabled && !loadingThis ? '暂无可回放数据' : '在 Foxglove 中打开回放'}>
            <button
              type="button"
              className="pe-fox-link-btn"
              disabled={disabled}
              onClick={() => openFoxglove(r)}
            >
              {loadingThis ? <Spin size="small" /> : <LinkOutlined />}
              foxglove
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      key: 'apiCode',
      width: 130,
      render: (v: string | null, r) => <ApiCodeLink code={v} source={r.source} />,
    },
    {
      title: '场景标签',
      dataIndex: 'label',
      key: 'label',
      width: 150,
      ellipsis: true,
      render: (v: string | null) => v || EMPTY,
    },
    ...(category !== 'ALL'
      ? []
      : [
          {
            title: '目标类型',
            key: 'categories',
            width: 170,
            ellipsis: true,
            render: (_: unknown, r: OdFpfnCase) =>
              categoryRows
                .filter((row) => row.caseId === r.caseId && row.category !== 'ALL' && (row.gt ?? 0) > 0)
                .map((row) => row.categoryCn && row.categoryCn !== row.category
                  ? `${row.categoryCn}(${row.category})`
                  : row.category)
                .join('、') || EMPTY,
          },
        ]),
    {
      title: isFp ? '标注误检目标' : '标注漏检目标',
      dataIndex: ['metrics', 'targetCount'],
      key: 'targetCount',
      width: 115,
      align: 'right',
      render: fmtCount,
    },
    {
      title: isFp ? '有效误检目标' : '有效漏检目标',
      dataIndex: ['metrics', 'eligibleTargetCount'],
      key: 'eligibleTargetCount',
      width: 115,
      align: 'right',
      render: fmtCount,
    },
    {
      title: '问题目标数',
      dataIndex: ['metrics', 'problemTargetCount'],
      key: 'problemTargetCount',
      width: 115,
      align: 'right',
      render: fmtCount,
    },
    {
      title: '帧对齐率',
      key: 'frameAlignmentRatio',
      width: 100,
      align: 'right',
      render: (_, r) => fmtRatio(frameAlignmentRatio(r.metrics)),
    },
    {
      title: isFp ? '复现帧率' : '匹配帧率',
      key: 'matchedFrameRatio',
      width: 110,
      align: 'right',
      render: (_, r) => fmtRatio(matchedFrameRatio(r.metrics)),
    },
    {
      title: '不通过原因',
      key: 'failReasons',
      width: 190,
      ellipsis: true,
      render: (_, r) =>
        reasonSummary(r.failReasonCounts, r, isFp, frameAlignmentRatio, matchedFrameRatio),
    },
    {
      title: '操作',
      key: 'action',
      width: 70,
      fixed: 'right',
      render: (_, r) => {
        const disabled = !r.taskId;
        return (
          <Space size={2}>
            <Tooltip title={disabled ? '该场景无 task，无日志' : 'Task 日志'}>
              <Button
                type="text"
                size="small"
                icon={<FileTextOutlined />}
                disabled={disabled}
                onClick={() => onViewLog(r.taskId)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Table<OdFpfnCase>
      rowKey="taskId"
      size="small"
      dataSource={visibleCases}
      columns={columns}
      tableLayout="fixed"
      scroll={{ x: 1500 }}
      pagination={visibleCases.length > 0 ? {
        defaultPageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100'],
        showQuickJumper: true,
        showTotal: (t) => `总共 ${t} 个 case`,
      } : false}
    />
  );
}
